import { useEffect, useState, type FC } from "react";
import { useApolloClient } from "@apollo/client/react";
import GET_CLIENT_INVOICES from "../../../graphql/queries/invoices/getClientInvoices";
import TitleUF from "../../atoms/typography/TitleUF";
import { theme } from "../../../theme";
import { useTranslation } from "react-i18next";
import { HorizontalSpacer } from "../../atoms/Spacers";
import ListTabBar from "../../molecules/ListTabBar";
import styled from "styled-components";
import FilterChip from "../../atoms/inputs/FilterChip";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import Card from "../../atoms/Card";
import CardBody from "../../atoms/CardBody";
import { RefreshIcon } from "../../atoms/icons/RefreshIcon";
import BodySmall from "../../atoms/typography/BodySmall";
import { Row } from "react-grid-system";
import { AddIcon } from "../../atoms/icons/AddIcon";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Subtitle from "../../atoms/typography/Subtitle";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import ActionButton from "../../atoms/buttons/ActionButton";
import Divider from "../../atoms/Divider";
import {
  SlidePanel,
  PanelHeader,
  PanelCloseButton,
  HighligtActionButton,
  panelUpVariants,
  panelDownVariants,
} from "../../atoms/SlideActionPanel";
import SlideActionToolbar from "../../molecules/SlideActionToolbar";
import {
  TagSlidePanel,
  NumberOfSelectedContainer,
} from "../../molecules/invoices/InvoiceToolbarPanels";
import Select from "../../atoms/inputs/Select";
import { useClientsWithInvoices } from "../../../graphql/hooks/invoices/useClientsWithInvoices";
import { useInvoiceList } from "../../molecules/invoices/useInvoiceList";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import Alert from "../../molecules/Alert";
import NoContentView from "../../molecules/NoContentView";
import ClientOverviewHeaderItem from "../../molecules/invoices/ClientOverviewHeaderItem";
import PaginationComponent from "../../molecules/PaginationComponent";
import type { InvoiceProcessStatus } from "../../../models/InvoiceStatusModel";
import {
  InvoiceAssessmentTags,
  InvoiceValidationTags,
} from "../../../models/InvoiceTags";
import { getInvoiceTagTitleKey } from "../../../utils/invoicesHelpers";
import InvoiceListFilterRow from "../../molecules/invoices/InvoiceListFilterRow";
import DeleteInvoicesButton from "../../molecules/invoices/DeleteInvoicesButton";

const FlexedDiv = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
`;

const tabs: Array<{ tabLabelKey: string; id: any }> = [
  {
    tabLabelKey: "invoices.all",
    id: "all",
  },
  {
    tabLabelKey: "invoices.underRegistration",
    id: "autopilotPause",
  },
  {
    tabLabelKey: "invoices.underValidation",
    id: "underValidation",
  },
  {
    tabLabelKey: "invoices.underAssessment",
    id: "underAssessment",
  },
  {
    tabLabelKey: "invoices.rejected",
    id: "rejected",
  },
  {
    tabLabelKey: "invoices.done",
    id: "done",
  },
];

const ITEMS_PER_PAGE = 10;

const InvoicesListPage: FC = () => {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const { page } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [expandedClientId, setExpandedClientId] = useState<string | undefined>(
    location.state?.expandedClientId,
  );

  const [selectedTab, setSelectedTab] = useState<
    | "all"
    | "underAssessment"
    | "underValidation"
    | "rejected"
    | "done"
    | "autopilotPause"
  >("all");
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | undefined
  >(undefined);
  const [selectedCompanyName, setSelectedCompanyName] = useState<
    string | undefined
  >(undefined);

  const {
    selectedInvoices,
    setSelectedInvoices,
    invoiceDateFrom,
    invoiceDateTo,
    dueDateFrom,
    dueDateTo,
    selectedAmounts,
    selectedTag,
    filterResetKey,
    openRejectModal,
    rejectModal,
    onInvoicesLoaded,
    getTagInitialState,
    onApplyTags,
    clearTag,
    onToggleTag,
    onSelectDates,
    onSelectAmount,
    onClearFilters,
  } = useInvoiceList({
    onFilterChange: () => setCurrentPageIndex(0),
    onClearExtra: () => {
      setSelectedCompanyId(undefined);
      setSelectedCompanyName(undefined);
    },
  });

  useEffect(() => {
    setCurrentPageIndex(0);
    setSelectedCompanyId(location.state?.selectedCompanyId ?? undefined);
    setSelectedCompanyName(location.state?.selectedCompanyName ?? undefined);
    switch (page) {
      case "assess":
        setSelectedTab("underAssessment");
        break;
      case "validate":
        setSelectedTab("underValidation");
        break;
      case "rejected":
        setSelectedTab("rejected");
        clearTag();
        break;
      case "done":
        setSelectedTab("done");
        clearTag();
        break;
      case "autopilotPause":
        setSelectedTab("autopilotPause");
        clearTag();
        break;
      default:
        setSelectedTab("all");
    }
  }, [page, location.state]);

  const tabInvoiceStatuses: InvoiceProcessStatus[] =
    selectedTab === "underAssessment"
      ? ["ASSESSMENT"]
      : selectedTab === "underValidation"
        ? ["VALIDATION"]
        : selectedTab === "rejected"
          ? ["REJECTED"]
          : selectedTab === "done"
            ? ["DONE"]
            : selectedTab === "autopilotPause"
              ? ["REGISTRATION"]
              : [
                  "ASSESSMENT",
                  "VALIDATION",
                  "REJECTED",
                  "DONE",
                  "REGISTRATION",
                ];

  const { clientsWithInvoices, error, loading, refetch } =
    useClientsWithInvoices(
      [...tabInvoiceStatuses],
      currentPageIndex * ITEMS_PER_PAGE,
      ITEMS_PER_PAGE,
      undefined,
      undefined,
      invoiceDateFrom,
      invoiceDateTo,
      dueDateFrom,
      dueDateTo,
      selectedAmounts?.min,
      selectedAmounts?.max,
      selectedTag ? [selectedTag] : undefined,
      selectedCompanyId,
    );

  const onClickItem = (item: {
    clientIdentifier: string;
    clientType: string;
    companyName: string;
  }) => {
    if (item.clientIdentifier) {
      setCurrentPageIndex(0);
      setSelectedInvoices([]);
      setSelectedCompanyId(item.clientIdentifier);
      setSelectedCompanyName(item.companyName);
    }
  };

  const numberOfPages = clientsWithInvoices
    ? Math.ceil(clientsWithInvoices.totalCount / ITEMS_PER_PAGE)
    : 0;

  return (
    <div>
      <TitleUF color={theme.colors.primary}>
        {selectedTab === "underAssessment"
          ? t("menu.invoiceAssessment")
          : selectedTab === "underValidation"
            ? t("menu.invoiceValidation")
            : t("clientCard.invoices")}
      </TitleUF>
      <HorizontalSpacer spacing={2} />
      <ListTabBar
        tabs={tabs}
        selectedId={selectedTab}
        onSelectTabId={(id) => {
          const path =
            id === "underAssessment"
              ? "assess"
              : id === "underValidation"
                ? "validate"
                : id === "rejected"
                  ? "rejected"
                  : id === "done"
                    ? "done"
                    : id === "autopilotPause"
                      ? "autopilotPause"
                      : "all";
          const validTags =
            id === "underAssessment"
              ? InvoiceAssessmentTags
              : id === "underValidation"
                ? InvoiceValidationTags
                : id === "all"
                  ? [...InvoiceAssessmentTags, ...InvoiceValidationTags]
                  : [];
          if (selectedTag && !validTags.includes(selectedTag)) {
            clearTag();
          }
          setSelectedInvoices([]);
          navigate(`/invoices/${path}`, {
            state: { selectedCompanyId, selectedCompanyName },
          });
        }}
      />

      <HorizontalSpacer spacing={1} />
      <Row nogutter align="center" justify="between">
        <FlexedDiv>
          {selectedTab !== "rejected" &&
            selectedTab !== "done" &&
            (selectedTab === "underAssessment"
              ? InvoiceAssessmentTags
              : selectedTab === "underValidation"
                ? InvoiceValidationTags
                : [...InvoiceAssessmentTags, ...InvoiceValidationTags]
            ).map((tag) => (
              <FilterChip
                key={tag}
                titleKey={getInvoiceTagTitleKey(tag)}
                selected={selectedTag === tag}
                onSelect={() => onToggleTag(tag)}
              />
            ))}
        </FlexedDiv>
      </Row>

      <HorizontalSpacer spacing={1} />

      <InvoiceListFilterRow
        invoiceDateFrom={invoiceDateFrom}
        invoiceDateTo={invoiceDateTo}
        dueDateFrom={dueDateFrom}
        dueDateTo={dueDateTo}
        onSelectDates={onSelectDates}
        onSelectAmount={onSelectAmount}
        selectedAmounts={selectedAmounts ?? undefined}
        onClearFilters={onClearFilters}
        filterResetKey={filterResetKey.toString()}
        onSelectClient={onClickItem}
        onClearClient={() => {
          setCurrentPageIndex(0);
          setSelectedInvoices([]);
          setSelectedCompanyId(undefined);
          setSelectedCompanyName(undefined);
        }}
        selectedCompanyName={selectedCompanyName}
      />

      <HorizontalSpacer spacing={1.5} />

      <Row nogutter justify="between" align="end">
        <div>
          {selectedTab !== "rejected" && selectedTab !== "done" && (
            <SlideActionToolbar
              minWidth={240}
              bgColor={theme.colors.white}
              actions={(openPanel, closePanel) => (
                <>
                  <NumberOfSelectedContainer>
                    <BodySmall color={theme.colors.textSecondary}>
                      {selectedInvoices.length}{" "}
                      {selectedInvoices.length === 1
                        ? t("common.selectedSingular").toLowerCase()
                        : t("common.selected").toLowerCase()}
                    </BodySmall>
                  </NumberOfSelectedContainer>
                  <HighligtActionButton
                    onClick={() => openPanel("tag")}
                    disabled={
                      selectedInvoices.length === 0 || selectedTab === "all"
                    }
                  >
                    <AddIcon />
                    <BodySmall>{t("invoices.tag")}</BodySmall>
                  </HighligtActionButton>
                  <HighligtActionButton
                    onClick={() => {
                      closePanel();

                      openRejectModal();
                    }}
                    disabled={selectedInvoices.length === 0}
                  >
                    <CrossIcon />
                    <BodySmall>{t("documentHandler.reject")}</BodySmall>
                  </HighligtActionButton>
                  <DeleteInvoicesButton
                    buttonType="highlightActionButton"
                    invoiceIds={selectedInvoices}
                    disabled={selectedInvoices.length === 0}
                  />
                </>
              )}
              panels={(isActivePanel, panelRef, panelDirection, closePanel) => (
                <>
                  {isActivePanel("tag") && (
                    <TagSlidePanel
                      key={`tag-${panelDirection}`}
                      panelRef={panelRef}
                      panelDirection={panelDirection}
                      closePanel={closePanel}
                      selectedCount={selectedInvoices.length}
                      tags={
                        selectedTab === "underAssessment"
                          ? InvoiceAssessmentTags.map((tag) => ({
                              key: tag,
                              labelKey: getInvoiceTagTitleKey(tag),
                              selected: getTagInitialState(tag),
                            }))
                          : selectedTab === "underValidation"
                            ? InvoiceValidationTags.map((tag) => ({
                                key: tag,
                                labelKey: getInvoiceTagTitleKey(tag),
                                selected: getTagInitialState(tag),
                              }))
                            : []
                      }
                      onApply={onApplyTags}
                    />
                  )}
                  {isActivePanel("reserve") && (
                    <SlidePanel
                      key={`reserve-${panelDirection}`}
                      ref={panelRef}
                      direction={panelDirection}
                      variants={
                        panelDirection === "up"
                          ? panelUpVariants
                          : panelDownVariants
                      }
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <PanelHeader>
                        <Subtitle color={theme.colors.primary}>
                          {t("invoices.reserveInvoice", {
                            count: selectedInvoices.length,
                          })}
                        </Subtitle>
                        <PanelCloseButton onClick={closePanel}>
                          <CrossIcon color={theme.colors.text} />
                        </PanelCloseButton>
                      </PanelHeader>
                      <Select
                        label={t("invoices.reserveTo")}
                        options={[
                          {
                            value: "colleague1",
                            label: "Anna",
                          },
                          {
                            value: "colleague2",
                            label: "Erik",
                          },
                        ]}
                        onChange={() => {}}
                      />
                      <HorizontalSpacer spacing={1} />
                      <Divider />
                      <HorizontalSpacer spacing={1} />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 24,
                        }}
                      >
                        <DefaultButton
                          title={t("common.cancel")}
                          onClick={closePanel}
                        />
                        <ActionButton
                          title={t("invoices.reserve")}
                          onClick={() => {}}
                          color={theme.colors.primary}
                        />
                      </div>
                    </SlidePanel>
                  )}
                </>
              )}
            />
          )}
          {rejectModal}
        </div>
        <FlexedDiv>
          <OutlinedButton
            title={t("common.refresh")}
            icon={<RefreshIcon />}
            color={theme.colors.primary}
            onClick={() => {
              refetch();
              apolloClient.refetchQueries({ include: [GET_CLIENT_INVOICES] });
            }}
          />
        </FlexedDiv>
      </Row>

      <HorizontalSpacer spacing={1.5} />

      <Card spacing={0} border>
        {error && (
          <CardBody>
            <Alert
              type="error"
              title={t("errors.couldNotFetchClientInvoices")}
              message={error?.toString()}
            />
          </CardBody>
        )}
        {loading ? (
          <CardBody>
            <LoadingSpinner />
          </CardBody>
        ) : (
          <div>
            {!clientsWithInvoices || clientsWithInvoices?.totalCount < 1 ? (
              <CardBody>
                <NoContentView />
              </CardBody>
            ) : (
              clientsWithInvoices?.items.map((clientWithInvoices) => (
                <ClientOverviewHeaderItem
                  key={clientWithInvoices.client.id}
                  selectedInvoices={selectedInvoices}
                  setSelectedInvoices={setSelectedInvoices}
                  clientIdentifier={clientWithInvoices.client.id}
                  clientName={clientWithInvoices.client.name}
                  statusCounts={clientWithInvoices.statusCounts}
                  filteredTotalAmountByCurrency={
                    clientWithInvoices.filteredTotalAmountByCurrency
                  }
                  numberOfInvoices={clientWithInvoices.filteredTotalCount}
                  invoiceDateFrom={invoiceDateFrom}
                  invoiceDateTo={invoiceDateTo}
                  dueDateFrom={dueDateFrom}
                  dueDateTo={dueDateTo}
                  invoiceStatuses={[...tabInvoiceStatuses]}
                  amountFrom={selectedAmounts?.min}
                  amountTo={selectedAmounts?.max}
                  selectedTags={selectedTag ? [selectedTag] : undefined}
                  onInvoicesLoaded={onInvoicesLoaded}
                  selectedTab={selectedTab}
                  isExpanded={expandedClientId === clientWithInvoices.client.id}
                  onToggleExpanded={() =>
                    setExpandedClientId((prev) => {
                      const isOpening = prev !== clientWithInvoices.client.id;
                      const isSwitching = isOpening && prev !== undefined;
                      if (isSwitching) setSelectedInvoices([]);
                      return isOpening
                        ? clientWithInvoices.client.id
                        : undefined;
                    })
                  }
                  unhandledDocumentCount={
                    clientWithInvoices.unhandledDocumentCount
                  }
                />
              ))
            )}
          </div>
        )}
        <PaginationComponent
          currentPageIndex={currentPageIndex}
          setCurrentPageIndex={setCurrentPageIndex}
          numberOfPages={numberOfPages}
          spacing={1}
        />
      </Card>
    </div>
  );
};

export default InvoicesListPage;
